import type { SelectFormat } from '@mpc/common/src/components/SForm/SFormElement';

export interface RefundStateMeta {
  label: string;
  textColor: string;
  bgColor: string;
  borderColor: string;
  /** Bedrock Tag 颜色（light-xxx），用于 Drawer title Tag，对齐 PrePayment stateNameColorMap */
  tagColor: 'orange' | 'brand' | 'green' | 'grey';
}

export const REFUND_STATE_MAP: Record<string, RefundStateMeta> = {
  PENDING_PLANNER_INITIATE: {
    label: '待策划发起',
    textColor: 'text-orange-600',
    bgColor: 'bg-orange-50',
    borderColor: 'border-orange-200',
    tagColor: 'orange',
  },
  PENDING_OPTIMIZER_INITIATE: {
    label: '待优化师发起',
    textColor: 'text-orange-600',
    bgColor: 'bg-orange-50',
    borderColor: 'border-orange-200',
    tagColor: 'orange',
  },
  PENDING_SUPPLIER_INITIATE: {
    label: '待供应商发起',
    textColor: 'text-orange-600',
    bgColor: 'bg-orange-50',
    borderColor: 'border-orange-200',
    tagColor: 'orange',
  },
  PENDING_PLANNER_CONFIRM: {
    label: '待策划确认',
    textColor: 'text-blue-600',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-200',
    tagColor: 'brand',
  },
  PENDING_OPTIMIZER_CONFIRM: {
    label: '待优化师确认',
    textColor: 'text-blue-600',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-200',
    tagColor: 'brand',
  },
  PENDING_SUPPLIER_CONFIRM: {
    label: '待供应商确认',
    textColor: 'text-blue-600',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-200',
    tagColor: 'brand',
  },
  COMPLETED: {
    label: '已完成',
    textColor: 'text-green-600',
    bgColor: 'bg-green-50',
    borderColor: 'border-green-200',
    tagColor: 'green',
  },
  ABANDONED: {
    label: '已废弃',
    textColor: 'text-gray-500',
    bgColor: 'bg-gray-50',
    borderColor: 'border-gray-200',
    tagColor: 'grey',
  },
};

export const DEFAULT_REFUND_STATE_META: RefundStateMeta = {
  label: '未知状态',
  textColor: 'text-gray-400',
  bgColor: 'bg-gray-50',
  borderColor: 'border-gray-200',
  tagColor: 'grey',
};

export const REFUND_INITIATOR_ROLE_LABEL: Record<string, string> = {
  PLANNER: '策划',
  OPTIMIZER: '优化师',
  SUPPLIER: '供应商',
};

export const REFUND_REGION_LABEL: Record<string, string> = {
  DOMESTIC: '国内',
  OVERSEA: '海外',
};

export const REFUND_BATCH_SIZE_LIMIT = 50;

/**
 * 人员选项类型 — 对齐 BaseUserVo（api.ts:4189）精简字段。
 * user_id 声明为 number|string 兼容 SelectFormat 泛型（value 入参为 string|number），
 * 避免直接套 BaseUserVo（其 user_id?: number）导致的 2322 类型冲突。
 */
export type RefundOptimizerVo = {
  user_id?: number | string;
  name?: string;
  user_name?: string;
};

/**
 * 人员类型下拉 — user_id ↔ 对象映射（对齐 TrumpetDetailFormModel.tsx:26 userSelectFormat）
 * 从 FormModel 提取共享：单个编辑（FormModel）与批量编辑（ProTableForm formColumns）共用，
 * 保证模型和交互对齐（详情 optimizers 对象数组 / 提交 optimizer_ids id 数组）。
 */
export const userSelectFormat: SelectFormat<RefundOptimizerVo> = {
  optionsToValue: (value, options) => {
    const option = options.find((item) => item.user_id === value);
    return {
      user_id: value,
      user_name: option?.name,
      name: option?.name,
      ...option,
    };
  },
  valueToSelectKey: (value) => value?.user_id ?? '',
};

// ─── 一级需求类型（l1_category_id）── 控制投放渠道/合作平台字段显隐（镜像充值 RechargeRefund/constants.ts） ─────

/** 效果买量 */
export const L1_CATEGORY_EFFECT_BUY = 19;
/** 收口投放 */
export const L1_CATEGORY_CONSTRAINED_DELIVERY = 20;
/** 品效推广 */
export const L1_CATEGORY_BRAND_EFFECT = 21;
/** 喇叭体系 */
export const L1_CATEGORY_TRUMPET = 22;

/** 显示「投放渠道」字段的一级需求类型（效果买量/收口投放/品效推广） */
export const CHANNEL_L1_CATEGORIES: ReadonlySet<number> = new Set([
  L1_CATEGORY_EFFECT_BUY,
  L1_CATEGORY_CONSTRAINED_DELIVERY,
  L1_CATEGORY_BRAND_EFFECT,
]);

/** 显示「合作平台」字段的一级需求类型（喇叭体系） */
export const PLATFORM_L1_CATEGORIES: ReadonlySet<number> = new Set([
  L1_CATEGORY_TRUMPET,
]);
